import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { mapGetters } from '@/store/lib/vue3helpers.js';
import { local_storage, LOCALSTORAGE_OPTIONS, localStorageKey } from '@/store/lib/local_storage';

const HEIGHT_MIN = 130;

export default function useEditorResize({ is_ticket, resize_bottom, editor_container, root_el, on_window_resize }) {
  const { current_profile: profile_current } = mapGetters(['current_profile']);
  const is_resizing = ref(false);
  const current_height = ref(window.innerHeight < 800 ? 150 : 185);
  const window_height = ref(window.innerHeight);

  let resize_direction = -1;
  let y_start = 0;
  let height_start = 0;
  let max_height = 500;
  let raf_id = null;
  let persist_timeout = null;
  let pending_height = null;
  let latest_client_y = 0;

  const editor_height_storage_key = computed(() => {
    if (!is_ticket.value) return null;
    return localStorageKey(profile_current.value, LOCALSTORAGE_OPTIONS.TICKETS_EDITOR_HEIGHT);
  });

  function getDefaultHeight() {
    return window.innerHeight < 800 ? 150 : 185;
  }

  function initializeHeightFromStorage() {
    const storage_key = editor_height_storage_key.value;
    const height_default = getDefaultHeight();
    if (!storage_key) {
      current_height.value = height_default;
      return;
    }
    const height_stored = local_storage.get(storage_key);
    const height_target = typeof height_stored === 'number' ? height_stored : height_default;
    const height_max = Math.max(window.innerHeight - 200, HEIGHT_MIN);
    current_height.value = Math.min(Math.max(height_target, HEIGHT_MIN), height_max);
  }

  function computeMaxHeight() {
    const height_fallback = Math.max(window.innerHeight - 200, HEIGHT_MIN);
    if (!is_ticket.value) return height_fallback;

    const el = root_el.value;
    if (!el) return height_fallback;

    const footer_el = el.closest('#ticket-footer');
    const container = footer_el.parentElement;
    if (!container) return height_fallback;

    const children = [...container.children];
    const footer_index = children.indexOf(footer_el);

    // Sum heights of fixed siblings (everything except the message body and the footer).
    // The message body is the child directly before the footer and can shrink freely.
    const height_fixed_siblings = children
      .filter((_, i) => i !== footer_index && i !== footer_index - 1)
      .reduce((sum, child) => sum + child.offsetHeight, 0);

    const footer_overhead = Math.max(footer_el.offsetHeight - current_height.value, 0);
    const gap = parseFloat(getComputedStyle(container).gap) || 0;
    const gaps_total = Math.max(children.length - 1, 0) * gap;
    const min_body_height = 80;

    return Math.max(
      container.clientHeight - height_fixed_siblings - min_body_height - footer_overhead - gaps_total,
      HEIGHT_MIN,
    );
  }

  function startResize(event) {
    is_resizing.value = true;
    resize_direction = resize_bottom.value ? 1 : -1;
    y_start = event.clientY;
    height_start = current_height.value;

    event.currentTarget.setPointerCapture(event.pointerId);
    max_height = computeMaxHeight();
    event.preventDefault();
  }

  function handleResize(event) {
    if (!is_resizing.value) return;
    latest_client_y = event.clientY;
    if (raf_id) return;

    raf_id = requestAnimationFrame(() => {
      const delta_y = latest_client_y - y_start;
      const height_new = height_start + delta_y * resize_direction;
      const height_clamped = Math.min(Math.max(height_new, HEIGHT_MIN), max_height);

      pending_height = height_clamped;
      const container = editor_container.value;
      if (container) {
        container.style.height = `${height_clamped}px`;
      }
      raf_id = null;
    });
  }

  function stopResize() {
    if (!is_resizing.value) return;
    is_resizing.value = false;

    if (pending_height !== null) {
      current_height.value = pending_height;
      pending_height = null;
    }

    if (!is_ticket.value) return;
    if (persist_timeout) clearTimeout(persist_timeout);
    persist_timeout = setTimeout(() => persistEditorHeight(), 1000);
  }

  function persistEditorHeight() {
    const storage_key = editor_height_storage_key.value;
    if (!storage_key) return;
    local_storage.set(storage_key, current_height.value);
  }

  function handleWindowResize() {
    window_height.value = window.innerHeight;
    on_window_resize();
  }

  function clampHeightToContainer() {
    if (!is_ticket.value) return;
    const height_max = computeMaxHeight();
    if (current_height.value > height_max) {
      current_height.value = height_max;
    }
  }

  watch(window_height, (height_new, height_old) => {
    const difference = height_new - height_old;
    if (difference < 0) {
      current_height.value = Math.max(current_height.value + difference, HEIGHT_MIN);
    }
  });

  initializeHeightFromStorage();

  onMounted(() => {
    document.addEventListener('pointermove', handleResize);
    document.addEventListener('pointerup', stopResize);
    window.addEventListener('resize', handleWindowResize);
    nextTick(() => clampHeightToContainer());
  });

  onBeforeUnmount(() => {
    if (raf_id) cancelAnimationFrame(raf_id);
    if (persist_timeout) clearTimeout(persist_timeout);
    document.removeEventListener('pointermove', handleResize);
    document.removeEventListener('pointerup', stopResize);
    window.removeEventListener('resize', handleWindowResize);
  });

  return {
    is_resizing,
    current_height,
    startResize,
  };
}
